import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import AnimatedBackground from "@/components/AnimatedBackground";
import { getPlanityUrl } from "@/config/standort";
import { Button } from "@/components/ui/button";
import { ArrowRight, Scissors, Sparkles, Zap, Eye, Palette } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import SEO from "@/components/SEO";

import hairImage from "@/assets/friseur-haarstyling-nuernberg.webp";
import facialImage from "@/assets/gesichtsbehandlung-facial-nuernberg.webp";
import haarentfernungImage from "@/assets/haarentfernung-glatte-haut-koerper.webp";
import lashesImage from "@/assets/wimpernverlaengerung-lashes-nuernberg.jpg";
import nailsImage from "@/assets/gelnaegel-nagelstudio-nuernberg.webp";


const services = [
  {
    title: "Haare",
    description: "Balayage, Coloration, Schnitt & Styling mit exklusiven Paul Mitchell Produkten.",
    image: hairImage,
    link: "/nuernberg/haare",
    icon: Scissors,
    tags: ["Balayage", "Coloration", "Styling", "Pflege"],
    priceFrom: "ab 35 €",
  },
  {
    title: "Gesichtsbehandlung",
    description: "Glow Solution, Aquafacial, Mikroneedling & iS Clinical – für sichtbar strahlende Haut.",
    image: facialImage,
    link: "/nuernberg/facials",
    icon: Sparkles,
    tags: ["Glow Solution", "Aquafacial", "Mikroneedling"],
    priceFrom: "ab 49 €",
  },
  {
    title: "Dauerhafte Haarentfernung",
    description: "Modernste Diodenlaser-Technologie mit dem ALIX Secret Twin – sanft, effektiv, für alle Hauttypen.",
    image: haarentfernungImage,
    link: "/nuernberg/haarentfernung",
    icon: Zap,
    tags: ["Diodenlaser", "Alle Hauttypen", "Schmerzarm"],
    priceFrom: "ab 29 €",
  },
  // Auskommentiert – kann bei Bedarf wieder aktiviert werden:
  // {
  //   title: "Lash- & Brow Lift",
  //   description: "Natürlicher Schwung für Wimpern und Augenbrauen – ganz ohne Extensions, hält bis zu 8 Wochen.",
  //   image: lashesImage,
  //   link: "/wimpern",
  //   icon: Eye,
  //   tags: ["Lash Lift", "Brow Lift", "Kombi"],
  //   priceFrom: "ab 12 €",
  // },
  {
    title: "Nägel",
    description: "Maniküre, Gelnägel & Shellack – kreative Designs, die deine Persönlichkeit widerspiegeln.",
    image: nailsImage,
    link: "/nuernberg/naegel",
    icon: Palette,
    tags: ["Maniküre", "Gelnägel", "Shellack"],
    priceFrom: "ab 35 €",
  },
];

const LeistungenPage = () => {
  const location = useLocation();
  const planityUrl = getPlanityUrl();
  return (
    <main className="min-h-screen bg-background relative">
      <SEO
        title="Leistungen & Preise – 22 Studios Nürnberg"
        description="Übersicht aller Behandlungen bei 22 Studios Nürnberg: Friseur, Facial, Nageldesign, Wimpernlifting & Laser-Haarentfernung. Preise & Infos auf einen Blick."
        canonical="https://22studios.de/nuernberg/leistungen"
      />
      <AnimatedBackground />
      <Navigation />

      {/* Hero */}
      <section className="pt-24 pb-10 md:pt-40 md:pb-20 relative z-10">
        <div className="container px-4 md:px-6 text-center max-w-3xl mx-auto">
          <span className="text-sm tracking-wide uppercase text-label font-medium">
            Unsere Behandlungen
          </span>
          <h1 className="mt-4 text-display md:text-display-lg font-serif font-light text-foreground">
            Alle <span className="text-highlight font-medium">Leistungen</span>
          </h1>
          <p className="mt-6 text-lg text-muted-foreground font-light leading-relaxed max-w-xl mx-auto">
            Von Haarstyling über Gesichtsbehandlungen bis hin zu Nageldesign – bei 22 Studios findest du alles unter einem Dach. Entdecke unser Angebot.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="pb-12 md:pb-28 relative z-10">
        <div className="container px-4 md:px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 max-w-6xl mx-auto">
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <Link
                  key={service.title}
                  to={service.link}
                  className="group bg-background rounded-2xl overflow-hidden shadow-soft hover-pop border border-border/30 hover:border-primary/30 transition-all duration-300"
                >
                  {/* Image */}
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-charcoal/50 via-transparent to-transparent" />
                    <span className="absolute bottom-4 right-4 px-3 py-1 rounded-full bg-background/90 backdrop-blur-sm text-xs font-semibold text-foreground">
                      {service.priceFrom}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="p-6 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center group-hover:bg-primary/40 transition-colors">
                        <Icon className="w-4 h-4 text-foreground" />
                      </div>
                      <h2 className="text-xl font-serif font-medium text-foreground">
                        {service.title}
                      </h2>
                    </div>
                    <p className="text-sm text-muted-foreground font-light leading-relaxed">
                      {service.description}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {service.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2.5 py-0.5 text-[11px] font-medium rounded-full bg-secondary/50 text-muted-foreground border border-border/30"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-1 text-sm font-medium text-highlight pt-1 group-hover:gap-2 transition-all">
                      Mehr erfahren
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* CTA */}
          <div className="text-center mt-10 md:mt-16">
            <Button
              size="lg"
              className="bg-primary text-primary-foreground hover:bg-blush-dark px-10 py-6 text-base font-medium rounded-full hover-lift shadow-glow"
              onClick={() => window.open(planityUrl, '_blank')}
            >
              Jetzt Termin buchen
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default LeistungenPage;
